import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from './endpoints'
import type {
  AdminDocumentsQuery,
  ConversationDetail,
  ConversationPatch,
  ConversationSummary,
  FeedbackRequest,
  GapStatus,
  KnowledgeGap,
  Message,
  Meta,
  NewUserRequest,
  SearchDebugRequest,
  UserPatch,
  WebsiteSourceRequest,
} from './types'

export const queryKeys = {
  meta: ['meta'] as const,
  conversations: ['conversations'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  admin: {
    all: ['admin'] as const,
    overview: ['admin', 'overview'] as const,
    sources: ['admin', 'sources'] as const,
    jobs: ['admin', 'jobs'] as const,
    documents: (query: AdminDocumentsQuery) => ['admin', 'documents', query] as const,
    gaps: (status: GapStatus) => ['admin', 'gaps', status] as const,
    feedback: ['admin', 'feedback'] as const,
    users: ['admin', 'users'] as const,
    departments: ['admin', 'departments'] as const,
  },
  library: (q: string, department: string | null) => ['library', q, department] as const,
  document: (id: number) => ['document', id] as const,
  uploads: (scope: 'mine' | 'all') => ['uploads', scope] as const,
}

// ------------------------------------------------------------------ meta

/** Meta is loaded (and seeded into the cache) by the bootstrap before the app renders. */
export function useMeta(): Meta {
  const { data } = useQuery({
    queryKey: queryKeys.meta,
    queryFn: ({ signal }) => api.getMeta({ signal }),
    staleTime: Infinity,
  })
  if (!data) throw new Error('useMeta() used before /api/meta was loaded')
  return data
}

// ------------------------------------------------------------------ conversations

export function useConversations() {
  return useQuery({
    queryKey: queryKeys.conversations,
    queryFn: ({ signal }) => api.listConversations(signal),
  })
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: queryKeys.conversation(id ?? ''),
    queryFn: ({ signal }) => api.getConversation(id as string, signal),
    enabled: Boolean(id),
  })
}

/** Cache helpers shared by mutations and the streaming controller. */
export const conversationCache = {
  upsertSummary(client: QueryClient, summary: ConversationSummary) {
    client.setQueryData<ConversationSummary[]>(queryKeys.conversations, (old) => {
      const rest = (old ?? []).filter((c) => c.id !== summary.id)
      return [summary, ...rest]
    })
  },

  patchSummary(client: QueryClient, id: string, patch: Partial<ConversationSummary>) {
    client.setQueryData<ConversationSummary[]>(queryKeys.conversations, (old) =>
      old?.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    )
    client.setQueryData<ConversationDetail>(queryKeys.conversation(id), (old) => (old ? { ...old, ...patch } : old))
  },

  /** Touch a conversation so it moves to the top of "Today". */
  touch(client: QueryClient, id: string, title?: string) {
    const now = new Date().toISOString()
    client.setQueryData<ConversationSummary[]>(queryKeys.conversations, (old) => {
      if (!old) return old
      const existing = old.find((c) => c.id === id)
      if (!existing) return old
      const updated = { ...existing, updated_at: now, ...(title ? { title } : {}) }
      return [updated, ...old.filter((c) => c.id !== id)]
    })
  },

  setMessages(client: QueryClient, id: string, update: (messages: Message[]) => Message[]) {
    client.setQueryData<ConversationDetail>(queryKeys.conversation(id), (old) =>
      old ? { ...old, messages: update(old.messages) } : old,
    )
  },

  patchMessage(client: QueryClient, conversationId: string, messageId: string, patch: Partial<Message>) {
    conversationCache.setMessages(client, conversationId, (messages) =>
      messages.map((m) => (m.id === messageId ? { ...m, ...patch } : m)),
    )
  },
}

export function useUpdateConversation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ConversationPatch }) => api.updateConversation(id, patch),
    onMutate: async ({ id, patch }) => {
      await client.cancelQueries({ queryKey: queryKeys.conversations })
      const previous = client.getQueryData<ConversationSummary[]>(queryKeys.conversations)
      conversationCache.patchSummary(client, id, patch)
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) client.setQueryData(queryKeys.conversations, context.previous)
    },
    onSuccess: (summary) => {
      conversationCache.patchSummary(client, summary.id, summary)
    },
  })
}

export function useDeleteConversation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deleteConversation(id),
    onMutate: async (id) => {
      await client.cancelQueries({ queryKey: queryKeys.conversations })
      const previous = client.getQueryData<ConversationSummary[]>(queryKeys.conversations)
      client.setQueryData<ConversationSummary[]>(queryKeys.conversations, (old) => old?.filter((c) => c.id !== id))
      return { previous }
    },
    onError: (_error, _id, context) => {
      if (context?.previous) client.setQueryData(queryKeys.conversations, context.previous)
    },
    onSuccess: (_data, id) => {
      client.removeQueries({ queryKey: queryKeys.conversation(id) })
    },
  })
}

export function useSendFeedback() {
  return useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: FeedbackRequest }) =>
      api.sendFeedback(messageId, body),
  })
}

// ------------------------------------------------------------------ admin

export function useAdminOverview() {
  return useQuery({
    queryKey: queryKeys.admin.overview,
    queryFn: ({ signal }) => api.admin.overview(signal),
  })
}

export function useAdminSources(poll: boolean) {
  return useQuery({
    queryKey: queryKeys.admin.sources,
    queryFn: ({ signal }) => api.admin.sources(signal),
    refetchInterval: (query) => (poll || query.state.data?.some((s) => s.running) ? 5_000 : false),
  })
}

export function useAdminJobs() {
  return useQuery({
    queryKey: queryKeys.admin.jobs,
    queryFn: ({ signal }) => api.admin.jobs(20, signal),
    refetchInterval: (query) =>
      query.state.data?.some((j) => j.status === 'queued' || j.status === 'running') ? 5_000 : false,
  })
}

export function useSyncSource() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.admin.syncSource(id),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.admin.jobs })
      void client.invalidateQueries({ queryKey: queryKeys.admin.sources })
    },
  })
}

/** Adding, editing or removing a website changes jobs, counts and which documents exist. */
function useSourceChange<TVars>(mutationFn: (vars: TVars) => Promise<unknown>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.admin.all })
      void client.invalidateQueries({ queryKey: ['library'] })
      void client.invalidateQueries({ queryKey: queryKeys.meta })
    },
  })
}

export function useAddWebsiteSource() {
  return useSourceChange((body: WebsiteSourceRequest) => api.admin.addWebsite(body))
}

export function useUpdateWebsiteSource() {
  return useSourceChange(({ id, patch }: { id: number; patch: Partial<WebsiteSourceRequest> }) =>
    api.admin.updateWebsite(id, patch),
  )
}

export function useRemoveSource() {
  return useSourceChange((id: number) => api.admin.removeSource(id))
}

export function useAdminDocuments(query: AdminDocumentsQuery) {
  return useQuery({
    queryKey: queryKeys.admin.documents(query),
    queryFn: ({ signal }) => api.admin.documents(query, signal),
    placeholderData: keepPreviousData,
  })
}

export function useAdminGaps(status: GapStatus) {
  return useQuery({
    queryKey: queryKeys.admin.gaps(status),
    queryFn: ({ signal }) => api.admin.gaps(status, signal),
  })
}

export function useResolveGap() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: KnowledgeGap['id']) => api.admin.resolveGap(id),
    onMutate: async (id) => {
      const key = queryKeys.admin.gaps('open')
      await client.cancelQueries({ queryKey: key })
      const previous = client.getQueryData<KnowledgeGap[]>(key)
      client.setQueryData<KnowledgeGap[]>(key, (old) => old?.filter((g) => g.id !== id))
      return { previous }
    },
    onError: (_error, _id, context) => {
      if (context?.previous) client.setQueryData(queryKeys.admin.gaps('open'), context.previous)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'gaps'] })
      void client.invalidateQueries({ queryKey: queryKeys.admin.overview })
    },
  })
}

export function useAdminFeedback() {
  return useQuery({
    queryKey: queryKeys.admin.feedback,
    queryFn: ({ signal }) => api.admin.feedback(50, signal),
  })
}

export function useSearchDebug() {
  return useMutation({
    mutationFn: (body: SearchDebugRequest) => api.admin.searchDebug(body),
  })
}

// ------------------------------------------------------------------ documents & uploads

export function useLibrary(q: string, department: string | null) {
  return useQuery({
    queryKey: queryKeys.library(q, department),
    queryFn: ({ signal }) => api.documents.library({ q, department, limit: 120 }, signal),
    placeholderData: keepPreviousData,
  })
}

export function useDocument(id: number | null) {
  return useQuery({
    queryKey: queryKeys.document(id ?? 0),
    queryFn: ({ signal }) => api.documents.get(id ?? 0, signal),
    enabled: id !== null,
    staleTime: 60_000,
  })
}

/** Uploads list; polls while anything is still being indexed. */
export function useUploads(scope: 'mine' | 'all', enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.uploads(scope),
    queryFn: ({ signal }) => api.uploads.list(scope, signal),
    enabled,
    refetchInterval: (query) => (query.state.data?.some((u) => u.status === 'processing') ? 2000 : false),
  })
}

export function useDeleteUpload() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.uploads.remove(id),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['uploads'] })
      void client.invalidateQueries({ queryKey: ['library'] })
      // Department counts come with meta.
      void client.invalidateQueries({ queryKey: queryKeys.meta })
    },
  })
}

// ------------------------------------------------------------------ people

export function useAdminUsers() {
  return useQuery({ queryKey: queryKeys.admin.users, queryFn: ({ signal }) => api.admin.users(signal) })
}

export function useAddUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: NewUserRequest) => api.admin.addUser(body),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.admin.users }),
  })
}

export function useUpdateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UserPatch }) => api.admin.updateUser(id, patch),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.admin.users }),
  })
}

export function useResetPassword() {
  return useMutation({ mutationFn: (id: string) => api.admin.resetPassword(id) })
}

export function useRemoveUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.admin.removeUser(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.admin.users }),
  })
}

// ------------------------------------------------------------------ departments

export function useAdminDepartments() {
  return useQuery({ queryKey: queryKeys.admin.departments, queryFn: ({ signal }) => api.admin.departments(signal) })
}

/** The list lives in meta (for the pickers) and is shown on sources, documents and the library. */
function useDepartmentChange<TVars>(mutationFn: (vars: TVars) => Promise<unknown>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.admin.all })
      void client.invalidateQueries({ queryKey: queryKeys.meta })
      void client.invalidateQueries({ queryKey: ['library'] })
      void client.invalidateQueries({ queryKey: ['uploads'] })
    },
  })
}

export function useAddDepartment() {
  return useDepartmentChange((name: string) => api.admin.addDepartment(name))
}

export function useRenameDepartment() {
  return useDepartmentChange(({ id, name }: { id: number; name: string }) => api.admin.renameDepartment(id, name))
}

export function useRemoveDepartment() {
  return useDepartmentChange((id: number) => api.admin.removeDepartment(id))
}
